<!-- CertCardFilterPanel — 재사용 가능한 필터 패널 쉘 (LotFlowDashboard 구조 미러링) -->
<!-- Slots: group1 (검색/식별), group2 (상태·기간), advanced (고급 패널) -->
<!-- Events: search, reset -->
<template>
  <div class="cert-filter-panel">
    <a-card class="cert-filter-card">
      <a-form layout="inline" class="cert-filter-form" :model="filters" @finish="onSearch">

        <!-- ── 그룹1: 검색/식별 ── -->
        <div v-if="hasGroup1" class="cert-filter-group">
          <span class="cert-filter-group-label">{{ $t('certCard_filter_group1_label') }}</span>
          <slot name="group1" />
        </div>

        <!-- ── 그룹2: 상태·기간 (AND 조건 설명 툴팁 포함) ── -->
        <div v-if="hasGroup2" class="cert-filter-group">
          <span class="cert-filter-group-label" style="display: inline-flex; align-items: center; gap: 4px;">
            {{ $t('certCard_filter_group2_label') }}
            <a-tooltip :title="$t('certCard_filter_and_tooltip')" placement="top">
              <question-circle-outlined style="font-size: 12px; color: rgba(0,0,0,0.35); cursor: default;" />
            </a-tooltip>
          </span>
          <slot name="group2" />
        </div>

        <!-- ── 액션 버튼 행 ── -->
        <div class="cert-filter-actions-row">
          <a-space>
            <a-button type="primary" html-type="submit">
              {{ $t('certCard_filter_search') }}
            </a-button>
            <a-button @click="onReset">
              {{ $t('certCard_filter_reset') }}
            </a-button>
            <!-- 고급 필터 토글 버튼 — advanced 슬롯이 제공된 경우만 표시 -->
            <a-button
              v-if="hasAdvanced"
              type="link"
              size="small"
              style="padding: 0; font-size: 12px;"
              @click="toggleAdvanced"
            >
              {{ $t('certCard_filter_advanced_toggle') }}
              <span style="margin-left: 2px;">{{ showAdvanced ? '▲' : '▼' }}</span>
            </a-button>
          </a-space>
          <!-- 고급 필터 활성 배지 (패널 접힌 상태 + 활성 카운트 > 0) -->
          <span
            v-if="hasAdvanced && !showAdvanced && advancedActiveCount > 0"
            class="cert-filter-advanced-badge"
          >
            {{ $t('certCard_filter_advanced_badge', { n: advancedActiveCount }) }}
          </span>
        </div>

        <!-- ── 고급 필터 패널 ── -->
        <div v-if="hasAdvanced && showAdvanced" class="cert-filter-group cert-filter-group--advanced">
          <span class="cert-filter-group-label cert-filter-group-label--advanced">
            {{ $t('certCard_filter_advanced_group_label') }}
          </span>
          <slot name="advanced" />
          <!-- 모바일 데스크탑 유도 알림 -->
          <a-alert
            v-if="isMobile"
            type="info"
            :message="$t('certCard_filter_mobile_hint')"
            show-icon
            style="margin-top: 8px; width: 100%;"
          />
        </div>

      </a-form>
    </a-card>
  </div>
</template>

<script>
// @MX:ANCHOR: [AUTO] CertCardFilterPanel — 재사용 가능한 인증서 필터 패널 (fan_in >= 3: Batches/Verify/Issue)
// @MX:REASON: [AUTO] 3개 CertCard 페이지에서 공유하는 LotFlow 스타일 필터 패널. group1/group2/advanced 슬롯 + search/reset 이벤트.
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

export default {
  name: 'CertCardFilterPanel',

  components: { QuestionCircleOutlined },

  props: {
    // 고급 필터 활성 카운트 — > 0이면 패널 접힌 상태에서 배지 표시
    advancedActiveCount: {
      type: Number,
      default: 0,
    },
  },

  emits: ['search', 'reset'],

  data() {
    return {
      // antd Form 은 :model 이 truthy 일 때만 @finish 를 emit 한다 — 빈 객체라도 반드시 정의.
      // (필드는 슬롯 소비자가 소유하므로 패널 자신은 검증할 값이 없다)
      filters: {},
      showAdvanced: false,
      isMobile: typeof window !== 'undefined' && window.innerWidth <= 767,
    };
  },

  computed: {
    // 슬롯 제공 여부 판별
    hasGroup1() {
      return !!this.$slots.group1;
    },
    hasGroup2() {
      return !!this.$slots.group2;
    },
    hasAdvanced() {
      return !!this.$slots.advanced;
    },
  },

  mounted() {
    window.addEventListener('resize', this._handleResize);
  },

  unmounted() {
    window.removeEventListener('resize', this._handleResize);
  },

  methods: {
    toggleAdvanced() {
      this.showAdvanced = !this.showAdvanced;
    },

    // Vue3 주의: defineEmits로 선언된 이벤트는 $attrs에서 제거됨 → 명시적 emit 필수
    onSearch() {
      this.$emit('search');
    },

    onReset() {
      this.showAdvanced = false;
      this.$emit('reset');
    },

    _handleResize() {
      if (this._resizeFrame) cancelAnimationFrame(this._resizeFrame);
      this._resizeFrame = requestAnimationFrame(() => {
        this.isMobile = window.innerWidth <= 767;
      });
    },
  },
};
</script>

<style scoped>
/* 패널 루트 — C1: sticky 제거 (스크롤 시 아래 구분선 가림 방지, 접기 토글과 레이아웃 충돌) */
.cert-filter-panel {
  margin-bottom: 16px;
}

/* 필터 카드 */
.cert-filter-card {
  border: 1px solid #f0f0f0;
}

/* 필터 폼 */
.cert-filter-form {
  flex-wrap: wrap;
  gap: 8px;
}

/* 필터 그룹 — LotFlow 동일 구조 */
.cert-filter-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;
}
.cert-filter-group:last-of-type {
  border-bottom: none;
}

/* 그룹 레이블 — 좌측 컬러 바 + 텍스트 (LotFlow 동일) */
.cert-filter-group-label {
  font-size: 12px;
  font-weight: 600;
  color: #595959;
  background: #fafafa;
  border-left: 3px solid #1677ff;
  padding: 1px 6px;
  border-radius: 0 2px 2px 0;
  white-space: nowrap;
  margin-right: 4px;
}

/* 고급 필터 그룹 — 구분 배경 */
.cert-filter-group--advanced {
  background: #fafafa;
  border-radius: 4px;
  padding: 8px 10px;
  margin-top: 4px;
}
.cert-filter-group-label--advanced {
  border-left-color: #722ed1;
}

/* 액션 버튼 행 */
.cert-filter-actions-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0 2px;
  width: 100%;
  flex-wrap: wrap;
}

/* 고급 필터 활성 배지 */
.cert-filter-advanced-badge {
  font-size: 12px;
  color: #722ed1;
  background: #f9f0ff;
  border: 1px solid #d3adf7;
  border-radius: 10px;
  padding: 1px 8px;
  margin-left: 4px;
}

/* 모바일 */
@media (max-width: 767px) {
  .cert-filter-actions-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
